/**
 * ThemeGenerateDialog (Sprint 15) — slim modal focused ONLY on AI generation.
 *
 * Replaces the full-editor behaviour of Sprint 14's ThemeCatalogDialog.
 * Renders:
 *   • CompositeDataPicker (audience / task / socialproof / insights)
 *   • AI hint textarea
 *   • Count slider 5-15 (default 7)
 *   • Generate button — fire-and-forget POST /generate-themes
 *
 * On submit: closes immediately. Generated themes land as drafts in
 * `theme_generations/{genId}/generatedThemes/*` (NOT in the canonical
 * catalog). User reviews + applies via the Themes tab.
 */

import React, { useState } from 'react';
import {
  Stack, Box, Typography, TextField, Button, Alert, Slider, InputAdornment,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { CompositeDataPicker } from '@modules/data-pickers';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { useCompositeDataOrchestrator } from '@modules/strategy-blocks/hooks/useCompositeDataOrchestrator';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import { useProjectDefaultModel } from '../hooks/useProjectDefaultModel';

const TESTIDS = {
  dialog: 'theme-generate-dialog',
  pickers: 'theme-generate-pickers',
  hint: 'theme-generate-hint',
  notes: 'theme-generate-notes',
  countSlider: 'theme-generate-count',
  modelSelector: 'theme-generate-model',
  submit: 'theme-generate-submit',
  cancel: 'theme-generate-cancel',
} as const;

export interface ThemeGenerateDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  strategyService: StrategyService;
  /** Optional: name + description snippet for the AI prompt. */
  projectContext?: string;
  /** Called after successful submission with the new taskId */
  onSuccess?: (result: { taskId: string }) => void;
}

export const ThemeGenerateDialog: React.FC<ThemeGenerateDialogProps> = ({
  open, onClose, projectId, userId, strategyService, projectContext, onSuccess,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [userHint, setUserHint] = useState('');
  const [userNotes, setUserNotes] = useState('');
  const [count, setCount] = useState<number>(7);
  const [model, setModel] = useState(projectDefaultModel.value);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);

  const orchestrator = useCompositeDataOrchestrator({
    projectId,
    strategyService,
    initialSelection: selectedPaths,
    selectAll: false,
    onSelectionChange: (d) => setSelectedPaths(d.selectedIds),
    open,
  });

  React.useEffect(() => {
    if (!open) {
      setUserHint('');
      setUserNotes('');
      setCount(7);
      setModel(projectDefaultModel.value);
      setError(null);
      setSubmitting(false);
    }
  }, [open, projectDefaultModel.value]);

  const selectionCount = React.useMemo(() => {
    const ids = orchestrator.aiSourceDataIds;
    if (!ids) return 0;
    return (
      (ids.audienceIds?.length ?? 0) +
      (ids.taskIds?.length ?? 0) +
      (ids.reviewIds?.length ?? 0) +
      (ids.studyCaseIds?.length ?? 0) +
      (ids.ratingIds?.length ?? 0) +
      (ids.bigNumberIds?.length ?? 0)
    );
  }, [orchestrator.aiSourceDataIds]);

  const handleSubmit = async () => {
    setError(null); setSubmitting(true);
    try {
      const client = new ContentPlanApiClient();
      // Concatenate short hint (filter/focus) + long notes (detailed guidance)
      // into a single userHint payload — the backend prompt treats both as
      // user-supplied context. We separate them in the UI for clarity.
      const combinedHint = [userHint.trim(), userNotes.trim()].filter(Boolean).join('\n\n---\nAdditional notes:\n');
      const response = await client.generateThemes({
        projectId, userId,
        sourceData: orchestrator.hydratedData,
        sourceDataIds: orchestrator.aiSourceDataIds as any,
        count,
        projectContext,
        userHint: combinedHint || undefined,
        models: model ? { themeGenerator: model } : undefined,
      });
      if (!response.success) throw new Error(response.error ?? 'theme generation failed');
      onSuccess?.({ taskId: response.taskId });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="md" data-testid={TESTIDS.dialog}>
      <DialogHeader
        title="Generate themes via AI"
        subtitle="Drafts will appear in the Themes tab for your review before adding to the catalog"
        onClose={onClose}
      />
      {/* Stable dialog height — content area reserves space so async picker
          data load doesn't resize the dialog mid-interaction. minHeight keeps
          layout stable while empty, maxHeight + overflow:auto prevents growth
          past viewport when picker populates fully. */}
      <DialogContent sx={{ minHeight: 560, maxHeight: '75vh', overflow: 'auto' }}>
        <Stack spacing={2.5} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Count control — numeric input + slider pair, visible above the fold
              so users don't have to scroll through the large source-data picker. */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>How many themes?</Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 2, px: 1 }}>
              <TextField
                type="number"
                value={count}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (Number.isNaN(n)) return;
                  setCount(Math.max(1, Math.min(50, n)));
                }}
                inputProps={{
                  min: 1, max: 50, step: 1,
                  'data-testid': 'theme-generate-count-input',
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">themes</InputAdornment>,
                }}
                size="small"
                sx={{ width: 140, flexShrink: 0 }}
              />
              <Slider
                value={Math.max(5, Math.min(15, count))}
                onChange={(_, v) => setCount(v as number)}
                step={1} min={5} max={15}
                valueLabelDisplay="auto"
                data-testid={TESTIDS.countSlider}
                sx={{ flex: 1, mx: 1 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Typed input accepts 1-50 (for edge cases); slider covers the recommended 5-15 range.
            </Typography>
          </Box>

          <Box data-testid={TESTIDS.pickers}>
            <Typography variant="subtitle2" gutterBottom>Source data</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Pick audiences / tasks / insights the AI should anchor themes on.
              Each generated theme will cite the entity IDs that drove it.
            </Typography>
            {/* Reserve a stable area for the picker so it doesn't collapse/expand
                while audiences/tasks/insights stream in from Firestore. Inner
                scroll keeps the dialog size unchanged regardless of entity count. */}
            <Box sx={{ minHeight: 300, maxHeight: 360, overflow: 'auto', borderRadius: 1 }}>
              <CompositeDataPicker
                {...orchestrator.compositeProps}
                pickers={[
                  { type: 'audience' as const, label: 'Audiences', enabled: true, selectAll: false },
                  { type: 'task' as const, label: 'Tasks', enabled: true, selectAll: false },
                  { type: 'socialproof' as const, label: 'Social Proof', enabled: true, selectAll: false },
                  { type: 'insights' as const, label: 'Insights', enabled: true, selectAll: false },
                ]}
              />
            </Box>
          </Box>

          <TextField
            label="Short focus (optional)"
            placeholder="e.g. focus on B2B SaaS audiences, skip promotional themes"
            value={userHint}
            onChange={(e) => setUserHint(e.target.value)}
            size="small"
            inputProps={{ 'data-testid': TESTIDS.hint }}
            helperText="One-liner that filters or focuses the theme pool"
          />

          <TextField
            label="Your notes for the AI (optional)"
            placeholder="Longer guidance — e.g. 'We are launching v2 of our product; themes should cover migration stories, cost reduction angles, and emphasise technical depth over marketing fluff.'"
            value={userNotes}
            onChange={(e) => setUserNotes(e.target.value)}
            multiline minRows={3}
            inputProps={{ 'data-testid': TESTIDS.notes }}
            helperText="Free-form context — passed directly into the AI prompt alongside source data"
          />

          <Box data-testid={TESTIDS.modelSelector}>
            <Typography variant="subtitle2" gutterBottom>Model</Typography>
            <AvailableModelSelector
              projectId={projectId}
              value={model}
              onChange={setModel}
              label="Theme generator model"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Override per run. Default <code>{model}</code>.
            </Typography>
          </Box>

          {selectionCount === 0 && (
            <Alert severity="info">
              Select at least one source-data entity above to enable AI generation.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel', onClick: onClose, 'data-testid': TESTIDS.cancel,
        } as any}
        primaryAction={{
          label: submitting ? 'Submitting…' : `Generate ${count} themes`,
          onClick: handleSubmit,
          disabled: submitting || selectionCount === 0,
          startIcon: <AutoFixHighIcon />,
          'data-testid': TESTIDS.submit,
        } as any}
      />
    </BaseDialog>
  );
};

export default ThemeGenerateDialog;
